/**
 * Confirm if permanent, undo if not. Archive and leave happen at once and
 * offer a five second Undo in one toast, which lives at the Workshop's root
 * so it survives moving between the Projects home, a project and a thread.
 *
 * Two kinds of undo, depending on what the API can take back:
 * - `show`: the change was sent at once and the API can reverse it (a project
 *   archived is restored with a status change). Undo sends the reversal.
 * - `hold`: the API cannot take it back (leaving a thread: only its owner can
 *   add you again). Nothing is sent for five seconds; the item is hidden
 *   meanwhile. Undo cancels the request; the timer, the toast's close, a newer
 *   action or leaving Workshop send it. Hiding the tab or closing the page
 *   sends it too, through `commitOnUnload` when given (a keepalive request
 *   that outlives the page), since an ordinary request can die with it.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { X, type LucideIcon } from "lucide-react";

export const WORKSHOP_UNDO_MS = 5000;

type ToastCopy = {
  icon: LucideIcon;
  /** "You left “hello”" */
  title: string;
  /** What happens now, and how it comes back. */
  line: string;
};

type ActiveToast = ToastCopy & {
  seq: number;
  expiresAt: number;
  undo: () => void;
  /** Present on a held action: sends it. */
  commit?: () => void;
  /** A held action's send for when the page is going away. */
  commitOnUnload?: () => void;
  /** The held item's id, hidden while the hold runs. */
  heldId?: string;
};

type WorkshopUndoApi = {
  /**
   * The change is already sent; `undo` reverses it through the API. Returns
   * the toast's key, for `dismiss` if the change itself fails.
   */
  show: (toast: ToastCopy & { undo: () => void }) => number;
  /**
   * Hold a change the API cannot reverse. `id` is hidden (see `isHeld`) until
   * it is sent or undone; `onUndo` runs only if the person takes it back.
   * `commitOnUnload` sends it when the page is hidden or closing.
   */
  hold: (
    toast: ToastCopy & {
      id: string;
      commit: () => void;
      commitOnUnload?: () => void;
      onUndo?: () => void;
    },
  ) => number;
  /** Take a toast down without its Undo (the change it offered to undo failed). */
  dismiss: (key: number) => void;
  isHeld: (id: string) => boolean;
};

const WorkshopUndoContext = createContext<WorkshopUndoApi | null>(null);
const WorkshopUndoToastContext = createContext<{
  toast: ActiveToast | null;
  undo: () => void;
  close: () => void;
} | null>(null);

/** Outside a provider (a harness, a lone component) actions simply run. */
const PASS_THROUGH: WorkshopUndoApi = {
  show: () => 0,
  hold: ({ commit }) => {
    commit();
    return 0;
  },
  dismiss: () => {},
  isHeld: () => false,
};

export function useWorkshopUndo(): WorkshopUndoApi {
  return useContext(WorkshopUndoContext) ?? PASS_THROUGH;
}

export function WorkshopUndoProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ActiveToast | null>(null);
  const [heldIds, setHeldIds] = useState<ReadonlySet<string>>(() => new Set());
  const activeRef = useRef<ActiveToast | null>(null);
  const timerRef = useRef<number | null>(null);
  const seqRef = useRef(0);

  const release = useCallback((heldId: string | undefined) => {
    if (!heldId) return;
    setHeldIds((prev) => {
      if (!prev.has(heldId)) return prev;
      const next = new Set(prev);
      next.delete(heldId);
      return next;
    });
  }, []);

  /**
   * End the current toast. A held action is sent unless `cancel`; `unloading`
   * sends it the way that survives the page going away.
   */
  const settle = useCallback(
    (cancel: boolean, unloading = false) => {
      const current = activeRef.current;
      if (!current) return;
      activeRef.current = null;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (!cancel) {
        if (unloading && current.commitOnUnload) current.commitOnUnload();
        else current.commit?.();
      }
      release(current.heldId);
      setToast(null);
    },
    [release],
  );

  const start = useCallback(
    (next: Omit<ActiveToast, "seq" | "expiresAt">): number => {
      // A newer action sends whatever was still held.
      settle(false);
      seqRef.current += 1;
      const active: ActiveToast = {
        ...next,
        seq: seqRef.current,
        expiresAt: Date.now() + WORKSHOP_UNDO_MS,
      };
      activeRef.current = active;
      if (active.heldId) {
        const id = active.heldId;
        setHeldIds((prev) => new Set(prev).add(id));
      }
      setToast(active);
      timerRef.current = window.setTimeout(() => settle(false), WORKSHOP_UNDO_MS);
      return active.seq;
    },
    [settle],
  );

  const api = useMemo<WorkshopUndoApi>(
    () => ({
      show: ({ undo, ...copy }) => start({ ...copy, undo }),
      hold: ({ id, commit, commitOnUnload, onUndo, ...copy }) =>
        start({ ...copy, heldId: id, commit, commitOnUnload, undo: () => onUndo?.() }),
      // Only a reversible toast is taken down this way; a held action keeps
      // its own clock, so nothing held is ever dropped by a dismiss.
      dismiss: (key) => {
        const current = activeRef.current;
        if (current?.seq === key && !current.commit) settle(true);
      },
      isHeld: (id) => heldIds.has(id),
    }),
    [start, settle, heldIds],
  );

  const undo = useCallback(() => {
    const current = activeRef.current;
    if (!current) return;
    settle(true);
    current.undo();
  }, [settle]);

  const close = useCallback(() => settle(false), [settle]);

  // Leaving the tab or the page sends a held action rather than dropping it;
  // so does leaving Workshop (unmount).
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") settle(false, true);
    };
    const onPageHide = () => settle(false, true);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      settle(false);
    };
  }, [settle]);

  const toastValue = useMemo(() => ({ toast, undo, close }), [toast, undo, close]);

  return (
    <WorkshopUndoContext.Provider value={api}>
      <WorkshopUndoToastContext.Provider value={toastValue}>
        {children}
      </WorkshopUndoToastContext.Provider>
    </WorkshopUndoContext.Provider>
  );
}

function secondsLeft(expiresAt: number): number {
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
}

/**
 * The toast itself: icon, what happened, how it comes back, "UNDO · 5" and a
 * close. Square. Mount it once where it should sit.
 */
export function WorkshopUndoToast() {
  const ctx = useContext(WorkshopUndoToastContext);
  const toast = ctx?.toast ?? null;
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!toast) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 250);
    return () => window.clearInterval(id);
  }, [toast]);

  if (!ctx || !toast) {
    return <div className="ws-undo-region" role="status" aria-live="polite" />;
  }

  const Icon = toast.icon;
  const left = secondsLeft(toast.expiresAt);

  return (
    <div className="ws-undo-region" role="status" aria-live="polite">
      <div key={toast.seq} className="ws-undo">
        <Icon className="ws-undo-icon" strokeWidth={1.7} aria-hidden="true" />
        <div className="ws-undo-text">
          <p className="ws-undo-title">{toast.title}</p>
          <p className="ws-undo-line">{toast.line}</p>
          <button type="button" className="ws-undo-action" onClick={ctx.undo}>
            Undo<span aria-hidden="true"> · {left}</span>
          </button>
        </div>
        <button
          type="button"
          className="ws-undo-close"
          aria-label="Close"
          onClick={ctx.close}
        >
          <X className="size-3.5" strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
